import React from "react";
import {
    Document,
    Page,
    Text,
    View,
    Image,
    StyleSheet,
    PDFDownloadLink,
} from "@react-pdf/renderer";

import schoolLogo from "./exellentkidslogo.png";
import defaultParentAvatar from "./parentnewavatar.jpg";

const SCHOOL_NAME = "Zion Praise Academy - CRM School System";
const SCHOOL_MOTTO = "Building Blocks To Build On";
const SCHOOL_WEBSITE = "https:excellentkidsfoundation.org/";

const styles = StyleSheet.create({
    page: {
        size: "A4",
        backgroundColor: "#eef2f6",
        padding: 22,
        fontFamily: "Helvetica",
        color: "#172033",
    },

    document: {
        backgroundColor: "#ffffff",
        borderWidth: 1,
        borderColor: "#d5dce5",
        borderRadius: 10,
        overflow: "hidden",
        minHeight: 797,
    },

    topAccent: {
        height: 8,
        backgroundColor: "#001f5b",
    },

    header: {
        paddingHorizontal: 24,
        paddingTop: 15,
        paddingBottom: 13,
        borderBottomWidth: 1,
        borderBottomColor: "#e2e7ed",
        flexDirection: "row",
        alignItems: "center",
    },

    logoBox: {
        width: 67,
        height: 67,
        borderWidth: 1,
        borderColor: "#d8dfe8",
        borderRadius: 8,
        backgroundColor: "#ffffff",
        padding: 5,
        justifyContent: "center",
        alignItems: "center",
        marginRight: 15,
    },

    logo: {
        width: 55,
        height: 55,
        objectFit: "contain",
    },

    headerInfo: {
        flex: 1,
    },

    schoolName: {
        fontSize: 17,
        fontWeight: "bold",
        color: "#001f5b",
        marginBottom: 3,
    },

    motto: {
        fontSize: 8.5,
        color: "#6b7280",
        marginBottom: 7,
    },

    officialBadge: {
        alignSelf: "flex-start",
        backgroundColor: "#f0f4f9",
        borderWidth: 1,
        borderColor: "#d8e0ea",
        borderRadius: 4,
        paddingHorizontal: 7,
        paddingVertical: 3,
    },

    officialBadgeText: {
        fontSize: 6.8,
        fontWeight: "bold",
        color: "#001f5b",
        letterSpacing: 0.5,
    },

    titleArea: {
        paddingHorizontal: 24,
        paddingTop: 13,
        paddingBottom: 10,
        alignItems: "center",
    },

    documentTitle: {
        fontSize: 15,
        fontWeight: "bold",
        color: "#111827",
        textAlign: "center",
        letterSpacing: 0.4,
    },

    documentSubtitle: {
        fontSize: 8,
        color: "#737c88",
        marginTop: 4,
        textAlign: "center",
    },

    profileArea: {
        marginHorizontal: 24,
        padding: 12,
        backgroundColor: "#f7f9fb",
        borderWidth: 1,
        borderColor: "#e1e6ec",
        borderRadius: 7,
        flexDirection: "row",
        alignItems: "center",
    },

    avatarContainer: {
        width: 67,
        height: 67,
        borderRadius: 34,
        borderWidth: 2,
        borderColor: "#001f5b",
        backgroundColor: "#ffffff",
        padding: 3,
        marginRight: 13,
    },

    avatar: {
        width: "100%",
        height: "100%",
        borderRadius: 32,
        objectFit: "cover",
    },

    profileInfo: {
        flex: 1,
    },

    accountLabel: {
        fontSize: 7,
        color: "#7b8490",
        textTransform: "uppercase",
        letterSpacing: 1,
        marginBottom: 3,
    },

    parentName: {
        fontSize: 14,
        fontWeight: "bold",
        color: "#001f5b",
        marginBottom: 5,
    },

    roleBadge: {
        alignSelf: "flex-start",
        backgroundColor: "#001f5b",
        borderRadius: 4,
        paddingHorizontal: 7,
        paddingVertical: 3,
    },

    roleBadgeText: {
        fontSize: 7,
        color: "#ffffff",
        fontWeight: "bold",
        textTransform: "uppercase",
        letterSpacing: 0.5,
    },

    section: {
        marginHorizontal: 24,
        marginTop: 12,
        borderWidth: 1,
        borderColor: "#dfe5eb",
        borderRadius: 6,
        overflow: "hidden",
    },

    sectionHeader: {
        backgroundColor: "#f0f3f7",
        paddingHorizontal: 10,
        paddingVertical: 6,
        borderBottomWidth: 1,
        borderBottomColor: "#dfe5eb",
    },

    sectionHeaderText: {
        fontSize: 7.5,
        fontWeight: "bold",
        color: "#001f5b",
        textTransform: "uppercase",
        letterSpacing: 0.7,
    },

    infoGrid: {
        flexDirection: "row",
    },

    infoColumn: {
        width: "50%",
    },

    infoColumnRight: {
        width: "50%",
        borderLeftWidth: 1,
        borderLeftColor: "#e4e8ed",
    },

    infoRow: {
        flexDirection: "row",
        minHeight: 25,
        borderBottomWidth: 1,
        borderBottomColor: "#edf0f3",
    },

    infoRowLast: {
        flexDirection: "row",
        minHeight: 25,
    },

    label: {
        width: "42%",
        paddingHorizontal: 8,
        paddingVertical: 6,
        backgroundColor: "#fafbfc",
        fontSize: 7,
        fontWeight: "bold",
        color: "#737d88",
    },

    value: {
        width: "58%",
        paddingHorizontal: 8,
        paddingVertical: 6,
        fontSize: 8,
        color: "#202733",
    },

    credentials: {
        marginHorizontal: 24,
        marginTop: 12,
        borderWidth: 1,
        borderColor: "#cfd8e3",
        borderRadius: 7,
        overflow: "hidden",
    },

    credentialsHeader: {
        backgroundColor: "#001f5b",
        paddingHorizontal: 11,
        paddingVertical: 7,
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
    },

    credentialsTitle: {
        color: "#ffffff",
        fontSize: 8,
        fontWeight: "bold",
        textTransform: "uppercase",
        letterSpacing: 0.6,
    },

    secureText: {
        color: "#dce7f5",
        fontSize: 6.5,
    },

    credentialRow: {
        flexDirection: "row",
        minHeight: 28,
        borderBottomWidth: 1,
        borderBottomColor: "#e4e8ed",
    },

    credentialRowLast: {
        flexDirection: "row",
        minHeight: 28,
    },

    credentialLabel: {
        width: "27%",
        paddingHorizontal: 9,
        paddingVertical: 7,
        backgroundColor: "#f5f7f9",
        fontSize: 7,
        fontWeight: "bold",
        color: "#66717d",
    },

    credentialValue: {
        width: "73%",
        paddingHorizontal: 9,
        paddingVertical: 7,
        fontSize: 9,
        fontWeight: "bold",
        color: "#111827",
    },

    websiteSection: {
        marginHorizontal: 24,
        marginTop: 12,
        borderWidth: 1,
        borderColor: "#d6dee8",
        borderRadius: 7,
        backgroundColor: "#f7f9fb",
        padding: 10,
        flexDirection: "row",
        alignItems: "center",
    },

    websiteIcon: {
        width: 30,
        height: 30,
        borderRadius: 15,
        backgroundColor: "#001f5b",
        justifyContent: "center",
        alignItems: "center",
        marginRight: 9,
    },

    websiteIconText: {
        color: "#ffffff",
        fontSize: 10,
        fontWeight: "bold",
    },

    websiteContent: {
        flex: 1,
    },

    websiteTitle: {
        fontSize: 8,
        fontWeight: "bold",
        color: "#001f5b",
        marginBottom: 3,
    },

    websiteInstruction: {
        fontSize: 7,
        color: "#69737f",
        marginBottom: 4,
        lineHeight: 1.3,
    },

    websiteUrl: {
        fontSize: 9,
        fontWeight: "bold",
        color: "#111827",
    },

    instruction: {
        marginHorizontal: 24,
        marginTop: 12,
        padding: 10,
        backgroundColor: "#fffaf0",
        borderWidth: 1,
        borderColor: "#eadfca",
        borderLeftWidth: 3,
        borderLeftColor: "#c58a24",
        borderRadius: 5,
    },

    instructionTitle: {
        fontSize: 8,
        fontWeight: "bold",
        color: "#76520d",
        marginBottom: 4,
    },

    instructionText: {
        fontSize: 7.2,
        color: "#625a4c",
        lineHeight: 1.4,
    },

    securityNotice: {
        marginHorizontal: 24,
        marginTop: 10,
        flexDirection: "row",
        alignItems: "center",
        paddingVertical: 6,
        paddingHorizontal: 8,
        backgroundColor: "#f3f6f9",
        borderRadius: 4,
    },

    securityIcon: {
        width: 20,
        height: 20,
        borderRadius: 10,
        backgroundColor: "#e4eaf1",
        justifyContent: "center",
        alignItems: "center",
        marginRight: 7,
    },

    securityIconText: {
        fontSize: 8,
        fontWeight: "bold",
        color: "#001f5b",
    },

    securityText: {
        flex: 1,
        fontSize: 6.8,
        color: "#6b7480",
        lineHeight: 1.3,
    },

    footer: {
        marginHorizontal: 24,
        marginTop: 10,
        paddingTop: 8,
        paddingBottom: 10,
        borderTopWidth: 1,
        borderTopColor: "#e2e6eb",
        alignItems: "center",
    },

    footerSchool: {
        fontSize: 7.5,
        fontWeight: "bold",
        color: "#001f5b",
        marginBottom: 2,
    },

    footerText: {
        fontSize: 6.5,
        color: "#7b838d",
        textAlign: "center",
    },
});

const normalizeParent = (parent = {}, credentials = {}) => {
    const user = parent.user || {};

    return {
        id:
            parent.id ||
            user.id ||
            "",

        username:
            parent.username ||
            user.username ||
            "",

        email:
            credentials.email ||
            parent.email ||
            user.email ||
            "",

        password:
            credentials.password ||
            parent.initial_password ||
            parent.initialPassword ||
            parent.password_display ||
            parent.password ||
            user.initial_password ||
            user.initialPassword ||
            "",

        role:
            parent.user_type ||
            parent.role ||
            user.user_type ||
            user.role ||
            "parent",

        gender:
            parent.gender ||
            user.gender ||
            "",

        firstName:
            parent.first_name ||
            parent.firstName ||
            user.first_name ||
            user.firstName ||
            "",

        lastName:
            parent.last_name ||
            parent.lastName ||
            user.last_name ||
            user.lastName ||
            "",

        fullName:
            parent.full_name ||
            parent.fullName ||
            user.full_name ||
            user.fullName ||
            parent.username ||
            user.username ||
            "Parent",

        profileImage:
            parent.profile_image ||
            parent.profileImage ||
            user.profile_image ||
            user.profileImage ||
            "",
    };
};

const getImageSource = (image) => {
    if (!image) {
        return defaultParentAvatar;
    }

    if (
        typeof image === "string" &&
        (
            image.startsWith("http://") ||
            image.startsWith("https://") ||
            image.startsWith("data:image/")
        )
    ) {
        return image;
    }

    return defaultParentAvatar;
};

const DisplayValue = ({ value, fallback = "Not provided" }) => (
    <Text>
        {value || fallback}
    </Text>
);

export const ParentAuthenticationDocument = ({
    parent,
    credentials = {},
}) => {
    const data = normalizeParent(
        parent,
        credentials
    );

    return (
        <Document
            title={`Parent Authentication - ${data.username || data.fullName}`}
            author={SCHOOL_NAME}
            subject="Official Parent Login Authentication Information"
        >
            <Page
                size="A4"
                style={styles.page}
            >
                <View style={styles.document}>

                    <View style={styles.topAccent} />

                    <View style={styles.header}>
                        <View style={styles.logoBox}>
                            <Image
                                src={schoolLogo}
                                style={styles.logo}
                            />
                        </View>

                        <View style={styles.headerInfo}>
                            <Text style={styles.schoolName}>
                                {SCHOOL_NAME}
                            </Text>

                            <Text style={styles.motto}>
                                {SCHOOL_MOTTO}
                            </Text>

                            <View style={styles.officialBadge}>
                                <Text style={styles.officialBadgeText}>
                                    OFFICIAL SCHOOL DOCUMENT
                                </Text>
                            </View>
                        </View>
                    </View>

                    <View style={styles.titleArea}>
                        <Text style={styles.documentTitle}>
                            PARENT PORTAL AUTHENTICATION
                        </Text>

                        <Text style={styles.documentSubtitle}>
                            Official login information for secure access to the school parent portal
                        </Text>
                    </View>

                    <View style={styles.profileArea}>
                        <View style={styles.avatarContainer}>
                            <Image
                                src={getImageSource(data.profileImage)}
                                style={styles.avatar}
                            />
                        </View>

                        <View style={styles.profileInfo}>
                            <Text style={styles.accountLabel}>
                                Parent Account Holder
                            </Text>

                            <Text style={styles.parentName}>
                                {data.fullName || "Parent"}
                            </Text>

                            <View style={styles.roleBadge}>
                                <Text style={styles.roleBadgeText}>
                                    Parent Account
                                </Text>
                            </View>
                        </View>
                    </View>

                    <View style={styles.section}>
                        <View style={styles.sectionHeader}>
                            <Text style={styles.sectionHeaderText}>
                                Parent Account Information
                            </Text>
                        </View>

                        <View style={styles.infoGrid}>
                            <View style={styles.infoColumn}>

                                <View style={styles.infoRow}>
                                    <Text style={styles.label}>
                                        FULL NAME
                                    </Text>

                                    <Text style={styles.value}>
                                        {data.fullName || "Not provided"}
                                    </Text>
                                </View>

                                <View style={styles.infoRow}>
                                    <Text style={styles.label}>
                                        USERNAME
                                    </Text>

                                    <Text style={styles.value}>
                                        {data.username || "Not provided"}
                                    </Text>
                                </View>

                                <View style={styles.infoRowLast}>
                                    <Text style={styles.label}>
                                        GENDER
                                    </Text>

                                    <Text style={styles.value}>
                                        {data.gender || "Not provided"}
                                    </Text>
                                </View>

                            </View>

                            <View style={styles.infoColumnRight}>

                                <View style={styles.infoRow}>
                                    <Text style={styles.label}>
                                        ACCOUNT ROLE
                                    </Text>

                                    <Text style={styles.value}>
                                        Parent
                                    </Text>
                                </View>

                                <View style={styles.infoRow}>
                                    <Text style={styles.label}>
                                        ACCOUNT ID
                                    </Text>

                                    <Text style={styles.value}>
                                        {data.id || "Not provided"}
                                    </Text>
                                </View>

                                <View style={styles.infoRowLast}>
                                    <Text style={styles.label}>
                                        STATUS
                                    </Text>

                                    <Text style={styles.value}>
                                        Active Parent Account
                                    </Text>
                                </View>

                            </View>
                        </View>
                    </View>

                    <View style={styles.credentials}>
                        <View style={styles.credentialsHeader}>
                            <Text style={styles.credentialsTitle}>
                                School Portal Login Credentials
                            </Text>

                            <Text style={styles.secureText}>
                                KEEP CONFIDENTIAL
                            </Text>
                        </View>

                        <View style={styles.credentialRow}>
                            <Text style={styles.credentialLabel}>
                                EMAIL / LOGIN
                            </Text>

                            <Text style={styles.credentialValue}>
                                {data.email || "Not available"}
                            </Text>
                        </View>

                        <View style={styles.credentialRow}>
                            <Text style={styles.credentialLabel}>
                                PASSWORD
                            </Text>

                            <Text style={styles.credentialValue}>
                                {data.password || "Not available"}
                            </Text>
                        </View>

                        <View style={styles.credentialRowLast}>
                            <Text style={styles.credentialLabel}>
                                ROLE
                            </Text>

                            <Text style={styles.credentialValue}>
                                Parent
                            </Text>
                        </View>
                    </View>

                    <View style={styles.websiteSection}>
                        <View style={styles.websiteIcon}>
                            <Text style={styles.websiteIconText}>
                                @
                            </Text>
                        </View>

                        <View style={styles.websiteContent}>
                            <Text style={styles.websiteTitle}>
                                VISIT THE SCHOOL WEBSITE
                            </Text>

                            <Text style={styles.websiteInstruction}>
                                Open Google and search or enter the following school website address:
                            </Text>

                            <Text style={styles.websiteUrl}>
                                {SCHOOL_WEBSITE}
                            </Text>
                        </View>
                    </View>

                    <View style={styles.instruction}>
                        <Text style={styles.instructionTitle}>
                            IMPORTANT LOGIN INSTRUCTIONS
                        </Text>

                        <Text style={styles.instructionText}>
                            Keep this document in a safe place. Visit the school website,
                            select the parent portal/login option, and use the email and
                            password provided above. Do not share your login credentials
                            with unauthorized persons. If the school portal provides a
                            password-change option, change your password after your first login.
                        </Text>
                    </View>

                    <View style={styles.securityNotice}>
                        <View style={styles.securityIcon}>
                            <Text style={styles.securityIconText}>
                                !
                            </Text>
                        </View>

                        <Text style={styles.securityText}>
                            SECURITY NOTICE: This document contains confidential account
                            information. Please do not photograph, copy, post, or share your
                            credentials with anyone who is not authorized to access the account.
                        </Text>
                    </View>

                    <View style={styles.footer}>
                        <Text style={styles.footerSchool}>
                            {SCHOOL_NAME}
                        </Text>

                        <Text style={styles.footerText}>
                            Official Parent Authentication Document • {SCHOOL_MOTTO}
                        </Text>

                        <Text style={styles.footerText}>
                            This document is issued for authorized school-parent communication and portal access.
                        </Text>
                    </View>

                </View>
            </Page>
        </Document>
    );
};

export const downloadParentAuthenticationPDF = async (
    parent,
    credentials = {}
) => {
    const data = normalizeParent(
        parent,
        credentials
    );

    const { pdf } = await import(
        "@react-pdf/renderer"
    );

    const blob = await pdf(
        <ParentAuthenticationDocument
            parent={parent}
            credentials={credentials}
        />
    ).toBlob();

    const url = URL.createObjectURL(blob);

    const anchor = document.createElement("a");

    const safeUsername = (
        data.username ||
        data.fullName ||
        "parent"
    )
        .toString()
        .trim()
        .replace(
            /[^a-zA-Z0-9_-]+/g,
            "_"
        );

    anchor.href = url;

    anchor.download =
        `Parent_Authentication_${safeUsername}.pdf`;

    document.body.appendChild(anchor);

    anchor.click();

    document.body.removeChild(anchor);

    setTimeout(() => {
        URL.revokeObjectURL(url);
    }, 1000);
};

export const ParentAuthenticationPDFDownloadLink = ({
    parent,
    credentials = {},
    children,
}) => {
    const data = normalizeParent(
        parent,
        credentials
    );

    const safeUsername = (
        data.username ||
        data.fullName ||
        "parent"
    )
        .toString()
        .trim()
        .replace(
            /[^a-zA-Z0-9_-]+/g,
            "_"
        );

    return (
        <PDFDownloadLink
            document={
                <ParentAuthenticationDocument
                    parent={parent}
                    credentials={credentials}
                />
            }
            fileName={
                `Parent_Authentication_${safeUsername}.pdf`
            }
        >
            {children}
        </PDFDownloadLink>
    );
};

export default ParentAuthenticationDocument;